import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Button, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { SectionContainer } from '../ui/SectionContainer';
import { CTA_CONTENT } from '../../constants';

/**
 * Call-to-action section with gradient background
 * Used at the bottom of pages to encourage demo requests or contact
 */
export const CTASection: React.FC = () => {
  const theme = useTheme();

  return (
    <SectionContainer variant="gradient">
      <Box sx={{ textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'white',
              fontSize: { xs: '1.75rem', md: '2.25rem' },
            }}
          >
            {CTA_CONTENT.title}
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            {CTA_CONTENT.description}
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              component={RouterLink}
              to="/demo"
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: theme.palette.primary.main,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[8],
                },
                transition: theme.transitions.create(
                  ['transform', 'background-color', 'box-shadow'],
                  {
                    duration: theme.transitions.duration.short,
                  }
                ),
              }}
            >
              {CTA_CONTENT.primaryCta}
            </Button>

            <Button
              component={RouterLink}
              to="/contact"
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateY(-2px)',
                },
                transition: theme.transitions.create(['transform', 'background-color'], {
                  duration: theme.transitions.duration.short,
                }),
              }}
            >
              {CTA_CONTENT.secondaryCta}
            </Button>
          </Box>
        </motion.div>
      </Box>
    </SectionContainer>
  );
};
